import { useState } from "react";
import { ChevronLeft, ChevronRight, FileText, Play, Maximize } from "lucide-react";
import { fileUrl } from "../api";

/** Vista tipo diapositiva (fondo oscuro) usada por el Visor del paciente y por los Casos clínicos. */
export default function ClinicalSlide({ heading, title, cuadro, signs, labs, labMedia = [], ekgMedia = [], ecoMedia = [], ecoText, diagnosis, dateLabel,
  labels = { images: "EKG", video: "Ecocardiograma", labs: "Laboratorios" } }) {
  const [i, setI] = useState(0);
  const ekg = ekgMedia.filter((m) => m.type === "image");
  const video = ecoMedia.find((m) => m.type === "video");
  const ecoImg = ecoMedia.find((m) => m.type === "image");
  const full = () => document.getElementById("pn-slide")?.requestFullscreen?.();

  return (
    <div id="pn-slide" className="pn-visor-card p-6 sm:p-10 pn-fade">
      <div className="flex items-start justify-between gap-4">
        <p className="font-bold text-base sm:text-lg" style={{ color: "var(--brand2)" }}>{heading}</p>
        <button onClick={full} className="text-white/50 hover:text-white pn-noprint" title="Pantalla completa"><Maximize size={18} /></button>
      </div>
      <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold text-white leading-tight">{title}</h1>
      <div className="mt-8 grid lg:grid-cols-2 gap-5">
        <Block title="Cuadro clínico" color="var(--accent)">
          <p className="text-slate-200 whitespace-pre-wrap leading-relaxed">{cuadro || "—"}</p>
          {signs && <p className="text-slate-400 text-sm mt-3">{signs}</p>}
        </Block>
        <Block title={`${labels.images} ${dateLabel ? `[${dateLabel}]` : ""}`} color="var(--accent)">
          {ekg.length ? (
            <div className="relative">
              <a href={fileUrl(ekg[i % ekg.length].path)} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden bg-white aspect-[16/9]">
                <img src={fileUrl(ekg[i % ekg.length].path)} alt={labels.images} className="w-full h-full object-contain" />
              </a>
              {ekg.length > 1 && <>
                <button onClick={() => setI((i - 1 + ekg.length) % ekg.length)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full p-2 text-white" style={{ background: "var(--brand)" }} aria-label="Anterior"><ChevronLeft size={16} /></button>
                <button onClick={() => setI((i + 1) % ekg.length)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-2 text-white" style={{ background: "var(--brand)" }} aria-label="Siguiente"><ChevronRight size={16} /></button>
                <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-2 py-0.5 rounded">{(i % ekg.length) + 1}/{ekg.length}</span>
              </>}
            </div>
          ) : <EmptyBox text={`Sin ${labels.images.toLowerCase()}`} />}
        </Block>
        <Block title={labels.labs} color="var(--brand2)">
          {labs && <p className="text-slate-200 whitespace-pre-wrap leading-relaxed mb-3">{labs}</p>}
          {labMedia.map((m) => (
            <a key={m.path} href={fileUrl(m.path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm hover:bg-white/10 mb-2" style={{ color: "var(--accent)" }}>
              <FileText size={16} className="shrink-0" /> <span className="truncate">{m.filename || "Archivo"}</span>
            </a>
          ))}
          {!labs && !labMedia.length && <p className="text-slate-500">—</p>}
        </Block>
        <Block title={labels.video} color="var(--brand2)">
          <div className="grid sm:grid-cols-2 gap-4 items-start">
            <div className="rounded-xl overflow-hidden bg-[#0A0F1D] aspect-video flex items-center justify-center">
              {video ? <video src={fileUrl(video.path)} controls className="w-full h-full object-contain" />
                : ecoImg ? <img src={fileUrl(ecoImg.path)} alt={labels.video} className="w-full h-full object-contain" />
                : <Play size={36} className="text-slate-600" />}
            </div>
            <p className="text-slate-200 whitespace-pre-wrap text-sm leading-relaxed">{ecoText || "—"}</p>
          </div>
        </Block>
      </div>
      {diagnosis && (
        <div className="mt-6 rounded-xl p-5" style={{ border: "1px solid color-mix(in srgb, var(--brand) 45%, transparent)", background: "color-mix(in srgb, var(--brand) 12%, transparent)" }}>
          <p className="font-bold text-sm uppercase tracking-wide mb-1" style={{ color: "var(--brand)" }}>{labels.diagnosis || "Diagnóstico"}</p>
          <p className="text-white/90 whitespace-pre-wrap">{diagnosis}</p>
        </div>
      )}
    </div>
  );
}

const Block = ({ title, color, children }) => (
  <div className="pn-visor-block" style={{ border: `1px solid color-mix(in srgb, ${color} 35%, transparent)` }}>
    <h3 className="font-bold text-xl sm:text-2xl mb-4" style={{ color }}>{title}</h3>
    {children}
  </div>
);
const EmptyBox = ({ text }) => <div className="rounded-xl bg-[#0A0F1D] aspect-[16/9] flex items-center justify-center text-slate-600 text-sm">{text}</div>;
